import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
	Box,
	Button,
	ButtonGroup,
	Card,
	CardContent,
	Grid,
	IconButton,
	Paper,
	Tooltip,
	Typography,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import ModeEditIcon from '@mui/icons-material/ModeEdit';

import { createQuote } from "../../../../apis/quote";
import { ImageViewer } from "../../../../components/ImageViewer";
import { Loader } from "../../../../components/Loader";
import { CustomDialog } from "../../../../components/modal/CustomDialog";
import { useCompanyData } from "../../../../hook/useCompanyData";
import { setQuoteAdminModels } from "../../../../store/actions";
import { RootState } from "../../../../store/store";
import { Model } from "../../../../utils/model";
import {
	transformOriginalOptions,
	transformOptions,
	Option,
} from "../../../../utils/option";
import {
	fetchAdminEmails,
	getOptionData1,
	getOptionData2,
	subTotal,
} from "../../../../utils/quote";
import { useGlobalSnackBar } from "../../../SnackbarContext";
import { getAll, sendQuote } from "../../../../apis/admin";
import { Input } from "../../../../components/Input";
import { getAllOption } from "../../../../apis/option";

export const QuoteBuilder: React.FC = () => {
	const navigate = useNavigate();

	const quoteModels = useSelector(
		(state: RootState) => state.adminAuth.quoteAdminModels
	);
	const currentUser = useSelector((state: RootState) => state.adminAuth.admin);

	const { company } = useCompanyData();

	const dispatch = useDispatch();

	const [openDeleteModal, setOpenDeleteModal] = useState<boolean>(false);
	const [openBuildModal, setOpenBuildModal] = useState<boolean>(false);
	const [finalizeQuoteDisabled, setFinalizeQuoteDisabled] = useState<boolean>(false);

	const [activeQuoteModel, setActiveQuoteModel] = useState<any>({});
	const [orderedOptions, setOrderedOptions] = useState<Option[]>([]);
	const [isLoading, setIsLoading] = useState<boolean>(true);
	const [ccEmails, setCCEmails] = useState<string[]>([]);
	const [memo, setMemo] = useState<string>();
	const [optReference, setOptReference] = useState<string>();
	const [options, setOptions] = useState<Option[]>([]);
	const [unRemovedOptions, setUnRemovedOptions] = useState<Option[]>([]);
	const [removedOptionNames, setRemovedOptionNames] = useState<string[]>([]);

	const { showSnackBar } = useGlobalSnackBar();

	const fetchAdmins = async () => {
		try {
			const adminsData = await getAll();
			setCCEmails(
				fetchAdminEmails(adminsData, JSON.parse(company.to_copy_admins))
			);
		} catch (error) {
			console.error("Failed to fetch company", error);
		}

		try {
			const options = await getAllOption();
			setOptions(options);
			const unRemovedOptions = options.filter((opt: any) => !opt.isRemoved);
			setUnRemovedOptions(unRemovedOptions);
		} catch (err) {
			console.error(err);
		}
	};

	useEffect(() => {
		if (!company) return;
		fetchAdmins();
	}, [company]);

	const makeOrderedOptionData = useCallback(() => {
		const orderedOptionData = transformOriginalOptions(
			transformOptions(options, true),
			true
		);
		const _orderedOptionData = orderedOptionData?.filter(
			(option: Option) => !option.is_category
		);
		setOrderedOptions(_orderedOptionData);
		setIsLoading(false);
	}, [options]);

	useEffect(() => {
		if (quoteModels) {
			makeOrderedOptionData();
			setMemo(quoteModels[0]?.memo)
			setOptReference(quoteModels[0]?.optReference)
		}
	}, [options, quoteModels]);

	useEffect(() => {
		const _orderedOptions = orderedOptions?.map((option: Option) =>
			activeQuoteModel?.chose_options?.find((id: string) => id === option.ID)
				? { ...option, isAdded: true }
				: { ...option, isAdded: false }
		);
		setOrderedOptions(_orderedOptions);
	}, [activeQuoteModel]);

	const handleSubtract = (ID: string) => {
		const _quoteModel = quoteModels.map((item: Model) =>
			item._id === ID ? { ...item, qty: item.qty ? item.qty - 1 : 1 } : item
		);
		dispatch(setQuoteAdminModels(_quoteModel));
	};

	const handleAdd = (ID: string) => {
		const _quoteModel = quoteModels.map((item: Model) =>
			item._id === ID ? { ...item, qty: item.qty ? item.qty + 1 : 2 } : item
		);
		dispatch(setQuoteAdminModels(_quoteModel));
	};

	const onDeleteModel = () => {
		const _quoteModels = quoteModels.filter(
			(model: { _id: string }) => model._id !== activeQuoteModel._id
		);
		dispatch(setQuoteAdminModels(_quoteModels));
		setOpenDeleteModal(false);
	};

	const getChoseOptions = useCallback(
		(choseOptions: any) => {
			return choseOptions?.map((choseOption: any, idx: number) => {
				const matchedOption = options.find((option: any) => option.ID === choseOption.ID);

				if (!matchedOption) return null;

				const isTruncated = matchedOption.option_name.length > 30;
				const truncatedName = isTruncated
					? `${matchedOption.option_name.slice(0, 30)}...`
					: matchedOption.option_name;

				const fullLabel =
					matchedOption.isQTYEnabled && choseOption?.QTYNumber > 1
						? `${truncatedName}(qty: ${choseOption?.QTYNumber})`
						: truncatedName;

				const chip = (
					<Box
						component="span"
						sx={{
							backgroundColor: "#e0e0e0",
							color: matchedOption.isRemoved ? "red" : "#000",
							fontSize: "0.75rem",
							fontWeight: 500,
							borderRadius: "8px",
							height: "auto",
							'&:hover': {
								backgroundColor: "#d0d0d0",
							},
							padding: "3px 8px",
							background: "white",
							border: "1px solid #ccc",
							display: "inline-block",
						}}
					>
						{fullLabel}
					</Box>
				);

				return isTruncated ? (
					<Tooltip key={idx} title={matchedOption.option_name}>
						{chip}
					</Tooltip>
				) : (
					<React.Fragment key={idx}>{chip}</React.Fragment>
				);
			}) ?? [];
		},
		[options]
	);

	useEffect(() => {
		if (!quoteModels || !options) return;

		const removedNames: string[] = [];

		quoteModels.forEach((model: any) => {
			model?.chose_options?.forEach((choseOption: any) => {
				const matchedOption = options.find((option: any) => option.ID === choseOption.ID);
				if (matchedOption?.isRemoved) {
					removedNames.push(matchedOption.option_name);
				}
			});
		});

		setRemovedOptionNames(removedNames);
	}, [quoteModels, options]);

	useEffect(() => {
		if (removedOptionNames.length) {
			showSnackBar(
				<>
					<b>The following options have been removed:</b>
					<br />
					{removedOptionNames.map((name, idx) => (
						<div key={idx}>{name}</div>
					))}
				</>,
				"error",
				30000
			);
		}
	}, [removedOptionNames]);

	const onBuildModal = async () => {
		const data = quoteModels.map((model: any) => ({
			model_id: {
				ID: model.ID,
				MSRP: model.MSRP,
				dealer_price: model.dealer_price,
			},
			option_ids: model.chose_options
				? getOptionData2(unRemovedOptions, model.chose_options)
				: [],
			qty: model.qty || 1,
		}));

		try {
			setFinalizeQuoteDisabled(true);
			const res = await createQuote({
				model_option_ids: JSON.stringify(data),
				dealer_id: "ADMIN",
				memo,
				opt_reference: optReference,
				admin_creator_id: currentUser.id,
			});

			try {
				const emailRes = await sendQuote({
					ccEmails: ccEmails,
					to: currentUser?.email,
					ID: res?.ID,
				});
				showSnackBar("Build successfully.");
			} catch (err: any) {
				showSnackBar("Quote saved, but email could not be sent. Please download your quote manually.", "warning");
			}

			dispatch(setQuoteAdminModels([]));
			navigate(`/quote/${res.ID}`);

		} catch (err) {
			showSnackBar("Something went wrong while finalizing the quote.", "error");
			setFinalizeQuoteDisabled(false);
		}
	};

	const getTotalDealerPrice = useMemo(() => {
		const transformedArray = quoteModels?.map((item: Model) => ({
			model: { ...item },
			options: getOptionData1(options, item.chose_options),
			qty: item?.qty || 1,
		}));
		return subTotal(transformedArray, company).toFixed(2);
	}, [options, quoteModels, company]);

	const getTotalMSRPPrice = useMemo(() => {
		const transformedArray = quoteModels?.map((item: Model) => ({
			model: { ...item },
			options: getOptionData1(options, item.chose_options),
			qty: item?.qty || 1,
		}));
		return subTotal(transformedArray, company, "MSRP").toFixed(2);
	}, [options, quoteModels, company]);

	if (isLoading) return <Loader isLoading={isLoading} />;

	return (
		<Box sx={{ maxWidth: 900, mx: "auto", px: 2, py: 3 }}>
			{/* Add Another System button */}
			<Box sx={{ display: "flex", justifyContent: "flex-end", mb: 3 }}>
				<Button variant="contained" onClick={() => navigate("/models_list")}>
					Add Another System
				</Button>
			</Box>

			{/* System Cards */}
			{quoteModels?.map((model: any, index: number) => (
				<Paper variant="outlined" key={model._id} sx={{ mb: 3, p: 2 }}>
					{/* Header row */}
					<Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
						<Typography variant="h6">
							System {index + 1} &mdash; {model.model_name}
						</Typography>
						<Box>
							<Tooltip title="Edit">
								<IconButton
									color="primary"
									onClick={() =>
										navigate(`/models/${model.ID}*${model._id}/customize`)
									}
								>
									<ModeEditIcon />
								</IconButton>
							</Tooltip>
							<Tooltip title="Delete">
								<IconButton
									color="error"
									onClick={() => {
										setActiveQuoteModel(model);
										setOpenDeleteModal(true);
									}}
								>
									<DeleteIcon />
								</IconButton>
							</Tooltip>
						</Box>
					</Box>

					{/* Body: two columns */}
					<Grid container spacing={2}>
						<Grid item xs={12} sm={5}>
							<ImageViewer
								images={
									model.images
										? JSON.parse(model.images)?.filter(
											(item: any) => item !== null
										)
										: []
								}
								height={180}
							/>
						</Grid>
						<Grid item xs={12} sm={7}>
							<Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
								Part #: {model.part_number}
							</Typography>
							<Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
								MSRP: ${Number(model.MSRP).toFixed(2)}
							</Typography>
							<Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
								{process.env.REACT_APP_DEALER_NAME} Price: ${Number(model.dealer_price).toFixed(2)}
							</Typography>

							{/* QTY Selector */}
							<Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1 }}>
								<Typography variant="body2" fontWeight={600}>QTY:</Typography>
								<ButtonGroup
									variant="contained"
									size="small"
									aria-label="quantity selector"
								>
									<Button
										onClick={() => handleSubtract(model._id)}
										sx={{ color: "black", backgroundColor: "white" }}
									>
										-
									</Button>
									<Button sx={{ color: "black", backgroundColor: "white" }}>
										{model.qty || 1}
									</Button>
									<Button
										onClick={() => handleAdd(model._id)}
										sx={{ color: "black", backgroundColor: "white" }}
									>
										+
									</Button>
								</ButtonGroup>
							</Box>

							<Typography variant="caption" color="text.secondary">
								{(process.env.REACT_APP_SERVER_TYPE === "spacex" || process.env.REACT_APP_SERVER_TYPE === "industrial") ? (
									<>
										Quantity discounts start at 10 duplicate units and further
										increase at 50+ units.
									</>
								) : (
									<>
										Quantity discounts start at 5 duplicate units and further
										increase at 10+ units.
									</>
								)}
							</Typography>
						</Grid>
					</Grid>

					{/* Chosen Options row */}
					<Box sx={{ mt: 2 }}>
						<Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
							Selected Options:
						</Typography>
						<Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
							{model?.chose_options?.length
								? getChoseOptions(model.chose_options)
								: <Typography variant="body2" color="text.secondary"><i>None</i></Typography>
							}
						</Box>
					</Box>
				</Paper>
			))}

			{/* Quote Summary */}
			{quoteModels && quoteModels.length > 0 && (
				<Paper variant="outlined" sx={{ p: 2 }}>
					<Grid container spacing={2}>
						<Grid item xs={12} sm={4}>
							<Typography variant="h6" gutterBottom>
								Running Totals
							</Typography>
							<Typography gutterBottom>
								MSRP: ${getTotalMSRPPrice}
							</Typography>
							<Typography gutterBottom>
								{process.env.REACT_APP_DEALER_NAME}: ${getTotalDealerPrice}
							</Typography>
						</Grid>
						<Grid item xs={12} sm={4}>
							<Input
								label="Opt. Reference"
								name=""
								sx={{ mb: 2, width: "100%" }}
								rows={4}
								value={optReference}
								onChange={(e: React.ChangeEvent<HTMLInputElement>) => setOptReference(e.target.value)}
							/>
						</Grid>
						<Grid item xs={12} sm={4}>
							<Input
								label="Memo"
								name="invite_message_description"
								sx={{ mb: 2, width: "100%" }}
								rows={4}
								value={memo}
								onChange={(e: React.ChangeEvent<HTMLInputElement>) => setMemo(e.target.value)}
							/>
						</Grid>
					</Grid>

					<Typography variant="body2" color="text.secondary" sx={{ mt: 2, textAlign: "center" }}>
						Totals do not reflect sales tax where applicable.
					</Typography>

					<Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, mt: 2 }}>
						<Button variant="contained" onClick={() => navigate('/preview')}>
							Preview Quote
						</Button>
						<Button variant="contained" onClick={() => setOpenBuildModal(true)}>
							Finalize Quote
						</Button>
					</Box>
				</Paper>
			)}

			<CustomDialog
				title="Are you sure you want to delete this model?"
				openModal={openDeleteModal}
				handleClose={() => setOpenDeleteModal(false)}
				handleAction={onDeleteModel}
				buttonTitle={"Yes"}
				children={undefined}
			/>

			<CustomDialog
				title="Are you sure you want to build this quote?"
				openModal={openBuildModal}
				handleClose={() => setOpenBuildModal(false)}
				handleAction={onBuildModal}
				buttonTitle={"Yes"}
				children={undefined}
				isDisabled={finalizeQuoteDisabled}
			/>
		</Box>
	);
};
